import React from "react"
import { createRoot } from "react-dom/client"

// Visual cursor indicator component
const CursorIndicator = ({ position, height, dragType, dragElement }) => {
  return (
    <div
      className="cursor-indicator-container"
      style={{
        position: "absolute",
        left: `${position.x}px`,
        top: `${position.y}px`,
        zIndex: 9999,
        pointerEvents: "none",
      }}
    >
      {/* Main cursor line */}
      <div
        className="cursor-indicator"
        style={{
          width: "2px",
          height: `${height}px`,
          backgroundColor: "#3b82f6",
          borderRadius: "1px",
          boxShadow: `0 0 4px rgba(59, 130, 246, 0.5)`,
          animation: "cursor-blink 1s infinite",
        }}
      />
      
      {/* Image thumbnail preview */}
      {dragType === "image" && dragElement && (
        <div
          className="drag-thumbnail"
          style={{
            position: "absolute",
            left: "15px",
            top: "-75px",
            width: "80px",
            height: "80px",
            border: "3px solid #3b82f6",
            borderRadius: "8px",
            overflow: "hidden",
            backgroundColor: "white",
            boxShadow: "0 8px 20px rgba(0, 0, 0, 0.25)",
            animation: "thumbnail-float 2s ease-in-out infinite",
            transform: "rotate(-5deg)",
          }}
        >
          <img
            src={dragElement.src}
            alt="Drag preview"
            style={{
              width: "100%",
              height: "100%",
              objectFit: "cover",
              pointerEvents: "none",
              userSelect: "none",
              transform: "rotate(5deg) scale(1.1)",
            }}
          />
          {/* Small indicator dot */}
          <div
            style={{
              position: "absolute",
              top: "-8px",
              right: "-8px",
              width: "16px",
              height: "16px",
              backgroundColor: "#3b82f6",
              borderRadius: "50%",
              border: "3px solid white",
              boxShadow: "0 2px 6px rgba(0, 0, 0, 0.2)",
            }}
          />
        </div>
      )}
    </div>
  )
}

// CSS styles injection
const injectStyles = () => {
  if (!document.getElementById("cursor-vis-styles")) {
    const style = document.createElement("style")
    style.id = "cursor-vis-styles"
    style.textContent = `
      @keyframes cursor-blink {
        0%, 50% { opacity: 1; }
        51%, 100% { opacity: 0.3; }
      }
      
      @keyframes thumbnail-float {
        0%, 100% { transform: rotate(-5deg) translateY(0px); }
        50% { transform: rotate(-5deg) translateY(-10px); }
      }
      
      .cursor-vis-dragging {
        cursor: none !important;
      }
      
      .cursor-vis-dragging * {
        cursor: none !important;
      }
      
      .drop-target-highlight {
        background-color: rgba(59, 130, 246, 0.1) !important;
        border: 1px dashed #3b82f6 !important;
        border-radius: 4px !important;
      }
    `
    document.head.appendChild(style)
  }
}

class CursorVisualizer {
  constructor({ container, onImageMoved }) {
    this.editor = { container }
    this.container = null
    this.root = null
    this.isDragging = false
    this.dragData = null
    this.currentPosition = null
    this.highlightedElement = null
    this.onImageMoved = onImageMoved // Callback for when image is moved
    
    this.init()
  }

  init() {
    injectStyles()
    this.createContainer()
    this.bindEvents()
  }

  createContainer() {
    this.container = document.createElement("div")
    this.container.className = "cursor-vis-container"
    this.container.style.position = "absolute"
    this.container.style.top = "0"
    this.container.style.left = "0"
    this.container.style.pointerEvents = "none"
    this.container.style.zIndex = "9999"
    
    document.body.appendChild(this.container)
    this.root = createRoot(this.container)
  }

  bindEvents() {
    const editorElement = this.editor.container || document.querySelector('[contenteditable="true"]')
    if (!editorElement) {
      console.warn("❌ CURSOR-VIS: No editor element found")
      return
    }


    this.makeImagesDraggable(editorElement)

    // Store bound functions to avoid issues with 'this'
    this.boundDragStart = this.handleDragStart.bind(this)
    this.boundDragOver = this.handleDragOver.bind(this)
    this.boundDragEnd = this.handleDragEnd.bind(this)
    this.boundDrop = this.handleDrop.bind(this)

    editorElement.addEventListener("dragstart", this.boundDragStart)
    editorElement.addEventListener("dragover", this.boundDragOver)
    editorElement.addEventListener("dragend", this.boundDragEnd)
    editorElement.addEventListener("drop", this.boundDrop)

    // Store editor element for cleanup
    this.editorElement = editorElement

    // Watch for new images
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === Node.ELEMENT_NODE) {
            if (node.tagName === "IMG") {
              this.makeImageDraggable(node)
            } else {
              const images = node.querySelectorAll("img")
              images.forEach(img => this.makeImageDraggable(img))
            }
          }
        })
      })
    })
    
    observer.observe(editorElement, { childList: true, subtree: true })
    this.mutationObserver = observer
  }

  makeImagesDraggable(container) {
    const images = container.querySelectorAll("img")
    images.forEach(img => this.makeImageDraggable(img))
  }

  generateImageId() {
    return 'img-' + Math.random().toString(36).substr(2, 9) + '-' + Date.now().toString(36)
  }

  linkImageWithCaption(img) {
    let imageId = img.getAttribute('data-image-id')
    if (!imageId) {
      imageId = this.generateImageId()
      img.setAttribute('data-image-id', imageId)
    }

    const figure = img.closest('figure')
    if (figure) {
      const caption = figure.querySelector('figcaption')
      if (caption) {
        caption.setAttribute('data-caption-for', imageId)
      }
    }

    return imageId
  }

  makeImageDraggable(img) {
    
    img.draggable = true
    img.style.cursor = "grab"
    this.linkImageWithCaption(img)
    
    let isDragStartPending = false
    let dragStartTimeout = null
    let mouseStartPos = null
    let hasMovedMouse = false
    
    img.addEventListener("mousedown", (e) => { 
      img.style.cursor = "grabbing"
      isDragStartPending = true
      hasMovedMouse = false
      mouseStartPos = { x: e.clientX, y: e.clientY }
      
      // Clear any existing timeout
      if (dragStartTimeout) {
        clearTimeout(dragStartTimeout)
      }
      
      // Only start drag if user moves mouse more than 5px AND dragstart doesn't fire
      const mouseMoveHandler = (moveEvent) => {
        const deltaX = Math.abs(moveEvent.clientX - mouseStartPos.x)
        const deltaY = Math.abs(moveEvent.clientY - mouseStartPos.y)
        
        if (deltaX > 5 || deltaY > 5) {
          hasMovedMouse = true
          
          // If dragstart doesn't fire within 100ms after movement, manually trigger drag state
          if (!dragStartTimeout) {
            dragStartTimeout = setTimeout(() => {
              if (isDragStartPending && !this.isDragging && hasMovedMouse) {
                this.manualDragStart(img, e)
              }
            }, 100)
          }
        }
      }
      
      document.addEventListener("mousemove", mouseMoveHandler, { once: false })
      
      // Clean up mouse move listener on mouseup
      const cleanup = () => {
        document.removeEventListener("mousemove", mouseMoveHandler)
      }
      document.addEventListener("mouseup", cleanup, { once: true })
    })
    
    img.addEventListener("mouseup", () => { 
      img.style.cursor = "grab"
      isDragStartPending = false
      hasMovedMouse = false
      mouseStartPos = null
      if (dragStartTimeout) {
        clearTimeout(dragStartTimeout)
        dragStartTimeout = null
      }
    })
    
    // Add direct dragstart listener to image for debugging
    img.addEventListener("dragstart", (e) => {
      isDragStartPending = false
      if (dragStartTimeout) {
        clearTimeout(dragStartTimeout)
        dragStartTimeout = null
      }
      // Call the main handler manually if it doesn't get called
      this.handleDragStart(e)
    })
  }
  
  manualDragStart(img, mouseEvent) {
    
    this.isDragging = true
    document.body.classList.add('cursor-vis-dragging')

    let elementToDrag = img
    const figureContainer = img.closest("figure")
    if (figureContainer) {
      elementToDrag = figureContainer
    }

    this.dragData = {
      element: elementToDrag,
      imageElement: img,
      type: this.getDragType(img),
      startTime: Date.now()
    }

    elementToDrag.classList.add("dragging-element")
    
    // Show cursor at current mouse position
    const position = this.calculateCursorPosition(mouseEvent)
    if (position) {
      this.showCursor(position)
    }
  }

  handleDragStart(e) {
    
    // Only handle if it's an image
    if (e.target.tagName !== "IMG") {
      return
    }
    
    this.isDragging = true
    document.body.classList.add('cursor-vis-dragging')

    let elementToDrag = e.target
    const figureContainer = e.target.closest("figure")
    if (figureContainer) {
      elementToDrag = figureContainer
    }

    this.dragData = {
      element: elementToDrag,
      imageElement: e.target,
      type: this.getDragType(e.target),
      startTime: Date.now()
    }

    elementToDrag.classList.add("dragging-element")


    // Hide default drag image if available
    if (e.dataTransfer) {
      e.dataTransfer.setData("text/plain", "")
      e.dataTransfer.effectAllowed = "move"
      
      const transparentImg = new Image()
      transparentImg.src = 'data:image/gif;base64,R0lGODlhAQABAIAAAAUEBAAAACwAAAAAAQABAAACAkQBADs='
      e.dataTransfer.setDragImage(transparentImg, 0, 0)
    }
  }

  handleDragOver(e) {
    e.preventDefault()
    e.stopPropagation()
    
    if (!this.isDragging || !this.dragData) {
      return
    }

    const position = this.calculateCursorPosition(e)
    if (position) {
      this.showCursor(position)
    }

    const dropTarget = this.findDropTarget(e.target)
    this.updateDropTargetHighlight(dropTarget)
  }

  handleDragEnd() {
    
    if (!this.isDragging) return

    this.isDragging = false
    document.body.classList.remove('cursor-vis-dragging')
    
    this.clearCursor()
    this.clearDropTargetHighlight()

    if (this.dragData && this.dragData.element && this.dragData.element.parentNode) {
      this.dragData.element.classList.remove("dragging-element")
    }

    this.dragData = null
  }

  handleDrop(e) {
    
    e.preventDefault()
    e.stopPropagation()

    if (!this.dragData || !this.isDragging) {
      return
    }

    this.isDragging = false
    document.body.classList.remove('cursor-vis-dragging')
    
    this.clearCursor()
    this.clearDropTargetHighlight()

    const { element, type } = this.dragData

    if (type === "image") {
      const range = this.getDropRange(e)
      if (range) {
        this.insertImageAtRange(element, range)
      } else {
      }
    }

    this.dragData = null
  }

  getDropRange(e) {
    let range
    if (document.caretRangeFromPoint) {
      range = document.caretRangeFromPoint(e.clientX, e.clientY)
    } else if (document.caretPositionFromPoint) {
      const caretPos = document.caretPositionFromPoint(e.clientX, e.clientY)
      if (caretPos) {
        range = document.createRange()
        range.setStart(caretPos.offsetNode, caretPos.offset)
        range.setEnd(caretPos.offsetNode, caretPos.offset)
      }
    }

    if (!range) {
      const editorElement = this.editor.container || document.querySelector('[contenteditable="true"]')
      if (editorElement) {
        range = document.createRange()
        range.selectNodeContents(editorElement)
        range.collapse(false)
      }
    }

    return range
  }

  insertImageAtRange(element, range) {
    try {
      // First remove the dragging-element class from the original element
      element.classList.remove("dragging-element")
      
      // Move the original element instead of cloning
      const elementToInsert = element
      
      // Handle caption association if this is a standalone image that needs to be wrapped in figure
      const imageId = this.getImageId(elementToInsert)
      const associatedCaption = imageId ? document.querySelector(`figcaption[data-caption-for="${imageId}"]`) : null
      
      if (associatedCaption && !elementToInsert.contains(associatedCaption)) {
        if (elementToInsert.tagName === "IMG") {
          // Create a figure wrapper and move the image into it
          const figure = document.createElement('figure')
          
          // Remove element from its current position first
          elementToInsert.remove()
          
          // Add to figure
          figure.appendChild(elementToInsert)
          
          // Move associated caption
          const captionClone = associatedCaption.cloneNode(true)
          this.applyCaptionStyles(captionClone)
          figure.appendChild(captionClone)
          associatedCaption.remove()
          
          this.insertElementAtRange(figure, range)
          
          // Make the moved image draggable again
          this.makeImageDraggable(elementToInsert)
          
          // Notify about the image move
          if (this.onImageMoved) {
            this.onImageMoved(elementToInsert)
          }
          
        } else if (elementToInsert.tagName === "FIGURE") {
          // Remove element from its current position first
          elementToInsert.remove()
          
          const existingCaption = elementToInsert.querySelector('figcaption')
          if (!existingCaption) {
            const captionClone = associatedCaption.cloneNode(true)
            this.applyCaptionStyles(captionClone)
            elementToInsert.appendChild(captionClone)
            associatedCaption.remove()
          }
          
          this.insertElementAtRange(elementToInsert, range)
          
          // Make the moved image draggable again
          const img = elementToInsert.querySelector('img')
          if (img) {
            this.makeImageDraggable(img)
            
            // Notify about the image move
            if (this.onImageMoved) {
              this.onImageMoved(img)
            }
          }
        }
      } else {
        // Remove element from its current position first
        elementToInsert.remove()
        
        // Insert at new position
        this.insertElementAtRange(elementToInsert, range)
        
        // Make the moved image draggable again
        const img = elementToInsert.tagName === "FIGURE" ? 
          elementToInsert.querySelector("img") : elementToInsert
        
        if (img) {
          this.makeImageDraggable(img)
          
          // Notify about the image move
          if (this.onImageMoved) {
            this.onImageMoved(img)
          }
        }
      }

    } catch (error) {
      console.error("❌ CURSOR-VIS: Drop failed", error)
      element.classList.remove("dragging-element")
    }
  }

  insertElementAtRange(element, range) {
    range.deleteContents()
    range.insertNode(element)
    
    // Add spacing
    const nextSibling = element.nextSibling
    if (!nextSibling || nextSibling.nodeType !== Node.TEXT_NODE) {
      const textNode = document.createTextNode('\n')
      element.parentNode.insertBefore(textNode, nextSibling)
    }
  }

  getImageId(element) {
    if (element.tagName === "IMG") {
      return element.getAttribute('data-image-id')
    } else if (element.tagName === "FIGURE") {
      const img = element.querySelector("img")
      return img ? img.getAttribute('data-image-id') : null
    }
    return null
  }

  applyCaptionStyles(caption) {
    caption.className = "image-caption"
    caption.style.fontStyle = "italic"
    caption.style.color = "#888888"
  }


  calculateCursorPosition(e) {
    const editorElement = this.editor.container || document.querySelector('[contenteditable="true"]')
    if (!editorElement) return null

    const editorRect = editorElement.getBoundingClientRect()
    
    let position = {
      x: e.clientX - 1, // Center the 2px cursor line
      y: e.clientY,
      height: 20
    }

    // Keep within editor bounds
    position.x = Math.max(editorRect.left, Math.min(position.x, editorRect.right - 2))
    position.y = Math.max(editorRect.top, Math.min(position.y, editorRect.bottom - position.height))

    return position
  }

  showCursor(position) {
    if (!position || !this.root) return
    
    
    this.currentPosition = position

    try {
      this.root.render(
        <CursorIndicator 
          position={position} 
          height={position.height}
          dragType={this.dragData?.type || "content"}
          dragElement={this.dragData?.imageElement || this.dragData?.element}
        />
      )
    } catch (error) {
      console.warn("❌ CURSOR-VIS: Cursor render failed", error)
    }
  }

  clearCursor() {
    
    if (this.root) {
      try {
        this.root.render(null)
      } catch (error) {
        console.warn("❌ CURSOR-VIS: Cursor clear failed", error)
      }
    }
    
    this.currentPosition = null
  }

  findDropTarget(element) {
    let current = element
    while (current && current !== document.body) {
      if (this.isValidDropTarget(current)) {
        return current
      }
      current = current.parentElement
    }
    return null
  }

  isValidDropTarget(element) {
    const validTags = ["P", "DIV", "SECTION", "ARTICLE", "MAIN", "HEADER", "FOOTER"]
    const validClasses = ["block-element", "paragraph", "content-block"]
    
    return (
      validTags.includes(element.tagName) ||
      validClasses.some(cls => element.classList.contains(cls)) ||
      element.contentEditable === "true"
    )
  }

  updateDropTargetHighlight(target) {
    this.clearDropTargetHighlight()
    
    if (target && target !== this.dragData?.element) {
      target.classList.add("drop-target-highlight")
      this.highlightedElement = target
    }
  }

  clearDropTargetHighlight() {
    if (this.highlightedElement) {
      this.highlightedElement.classList.remove("drop-target-highlight")
      this.highlightedElement = null
    }
  }

  getDragType(element) {
    if (element.tagName === "IMG") return "image"
    if (element.classList.contains("block-element")) return "block"
    if (element.tagName === "P") return "paragraph"
    if (element.tagName === "H1" || element.tagName === "H2" || element.tagName === "H3") return "heading"
    return "content"
  }

  destroy() {
    
    document.body.classList.remove('cursor-vis-dragging')
    
    // Remove event listeners
    if (this.editorElement && this.boundDragStart) {
      this.editorElement.removeEventListener("dragstart", this.boundDragStart)
      this.editorElement.removeEventListener("dragover", this.boundDragOver)
      this.editorElement.removeEventListener("dragend", this.boundDragEnd)
      this.editorElement.removeEventListener("drop", this.boundDrop)
    }
    
    if (this.root) {
      // Use setTimeout to avoid unmounting during render
      setTimeout(() => {
        if (this.root) {
          this.root.unmount()
          this.root = null
        }
      }, 0)
    }
    
    if (this.container && this.container.parentNode) {
      this.container.parentNode.removeChild(this.container)
      this.container = null
    }
    
    if (this.mutationObserver) {
      this.mutationObserver.disconnect()
      this.mutationObserver = null
    }
    
    this.clearDropTargetHighlight()
    
    // Only remove styles if no other instance exists
    const styles = document.getElementById("cursor-vis-styles")
    if (styles && styles.parentNode && !globalVisualizer) {
      styles.parentNode.removeChild(styles)
    }
  }
}

// Plugin initialization with singleton pattern
let globalVisualizer = null

/**
 * Cursor visualizer plugin following Commander pattern
 * @param {Object} params - Plugin parameters
 * @param {string} params.action - Action type (init|destroy|getVisualizer)
 * @param {Object} params.data - Action data
 */
const CursorVisPlugin = ({ action, data }) => {
  switch (action) {
    case 'init':
      // Destroy existing instance first
      if (globalVisualizer) {
        globalVisualizer.destroy()
        globalVisualizer = null
      }

      if (data?.container) {
        globalVisualizer = new CursorVisualizer({
          container: data.container,
          onImageMoved: data.onImageMoved
        })
        return globalVisualizer
      }
      break

    case 'destroy':
      if (globalVisualizer) {
        globalVisualizer.destroy()
        globalVisualizer = null
      }
      break

    case 'getVisualizer':
      return globalVisualizer

    default:
      console.warn(`Unknown cursor-vis action: ${action}`)
      break
  }
}

export default CursorVisPlugin